// app/src/pages/admin/clause-checklists/components/clause-checklists-table/ClauseChecklistsTable.tsx
import { IClauseChecklistList, readClauseChecklists } from "@/api";
import { AdminPageHeader, SearchField } from "@/components/admin";
import { ClauseChecklistTitleCell } from "@/components/admin/table/cells/ClauseChecklistTitleCell";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { LinkRouter } from "@/components/shared/buttons/LinkRouter";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { formatDate, handleRowSelection } from "@/utils";
import {
    GridColDef,
    GridRowSelectionModel,
    GridSortModel,
} from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AddNewClauseChecklistButton } from "../AddNewClauseChecklistButton";
import { ClauseChecklistTableToolbarActions } from "./ClauseChecklistTableToolbarActions";
import { DeleteClauseChecklistButton } from "./DeleteClauseChecklistButton";

const columns: GridColDef<IClauseChecklistList>[] = [
    {
        field: "name",
        headerName: "Title",
        display: "flex",
        renderCell: ({ row }) => (
            <ClauseChecklistTitleCell clauseChecklist={row} />
        ),
    },
    {
        field: "description",
        headerName: "Description",
        display: "flex",
        maxWidth: 300,
    },
    {
        field: "organizations",
        headerName: "Organization",
        display: "flex",
        valueGetter: (_value, row) => row.organization_sort_name ?? "",
    },
    {
        field: "organization_group",
        headerName: "Group",
        display: "flex",
        sortable: false,
        maxWidth: 240,
        renderCell: ({ row }) =>
            row.organization_group && (
                <LinkRouter
                    to={`/admin/organization-groups/${row.organization_group.id}/details`}
                    sx={{ minWidth: 0 }}
                >
                    <Typography
                        color="primary"
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        title={row.organization_group.name}
                    >
                        {row.organization_group.name}
                    </Typography>
                </LinkRouter>
            ),
    },
    {
        field: "updated_at",
        headerName: "Last Updated",
        type: "date",
        display: "flex",
        valueFormatter: (value) => {
            return formatDate(value);
        },
    },
    {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        getActions: ({ row }) => [
            <DeleteClauseChecklistButton clauseChecklistId={row.id} />,
        ],
    },
];

/**
 * Table displaying Clause Checklists and actions
 */
export const ClauseChecklistsTable = () => {
    const navigate = useNavigate();
    const [selectedClauseChecklistsIds, setSelectedClauseChecklistsIds] =
        useState<number[]>([]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedClauseChecklistsIds(
            handleRowSelection(rowSelectionModel, rows)
        );
    };

    const [rows, setRows] = useState<IClauseChecklistList[]>([]);

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: "id", sort: "desc" },
    ]);

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const autoSizeOptions = {
        columns: [
            "name",
            "description",
            "organizations",
            "organization_group",
            "actions",
            "updated_at",
        ],
        includeHeaders: true,
        expand: true,
    };

    // Fetch Clause Checklists from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-clause-checklists",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            readClauseChecklists(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || undefined
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <AdminPageHeader
                title="Checklists"
                subtitle="Configure clause checklists for scope analysis"
            >
                <ActionButton
                    variant="outlined"
                    startIcon={<Sparkles size={16} />}
                    onClick={() => navigate("/admin/checklists/generate")}
                >
                    AI Generate
                </ActionButton>
                <AddNewClauseChecklistButton />
                <ClauseChecklistTableToolbarActions
                    selectedClauseChecklistIds={selectedClauseChecklistsIds}
                    rows={rows}
                />
            </AdminPageHeader>

            <div className="mt-6">
                <div className="mb-4">
                    <SearchField
                        value={search}
                        onChange={setSearch}
                        onClear={handleSearchClear}
                        placeholder="Search checklists..."
                    />
                </div>
                <Table
                    loading={isFetching}
                    columns={columns}
                    rows={rows}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    disableColumnResize
                    disableColumnFilter
                    autosizeOptions={autoSizeOptions}
                    autoSize
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode={"server"}
                    sortingMode={"server"}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </div>
        </>
    );
};
